import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import CrafterAnalytics from './CrafterAnalytics';
import {
  Package,
  Truck,
  MessageSquare,
  Plus,
  ChevronRight,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Flame,
  BarChart3,
  Sparkles,
  Send,
  Inbox,
  CreditCard,
  AlertCircle,
  Wallet
} from 'lucide-react';

export default function CrafterHomeDashboard({ 
  orders = [], 
  requests = [], 
  directRequests = [],
  proposals = [],
  conversations = [],
  recentActivity = [],
  stripeConnected = false,
  stripeLoading = false,
  pendingBalance = { amount: 0, ordersCount: 0 },
  onNavigate 
}) {
  const navigate = useNavigate();

  // Calculate metrics
  // "Ordini attivi" = tutti gli ordini non ancora completati/consegnati
  const activeOrders = orders.filter(o => ['pending', 'confirmed', 'in_progress', 'ready', 'shipped'].includes(o.status));
  const ordersToShip = orders.filter(o => ['confirmed', 'ready'].includes(o.status));
  const unansweredRequests = [...requests, ...directRequests].filter(r => r.status === 'open' || r.status === 'pending');
  const activeConversations = conversations.filter(c => c.has_unread);
  // "Completati" = ordini consegnati/completati
  const completedOrders = orders.filter(o => ['delivered', 'completed'].includes(o.status));
  // "In lavorazione" = ordini con status in_progress
  const inProgressOrders = orders.filter(o => o.status === 'in_progress');

  const urgentItems = [
    { count: unansweredRequests.length, label: 'richieste senza risposta', color: 'red', section: 'client-requests' },
    { count: ordersToShip.length, label: 'ordini da spedire', color: 'yellow', section: 'orders' },
    { count: activeConversations.length, label: 'conversazioni attive', color: 'green', section: 'messages' }
  ].filter(item => item.count > 0);

  return (
    <div className="space-y-6" data-testid="crafter-home-dashboard">
      {/* Stripe Registration Banner (stesso copy del banner sticky delle altre sezioni) */}
      {!stripeConnected && !stripeLoading && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-xl shadow-md overflow-hidden ${
            pendingBalance.ordersCount > 0
              ? 'bg-gradient-to-r from-amber-500 to-orange-500'
              : 'bg-gradient-to-r from-red-500 via-orange-500 to-amber-500'
          }`}
        >
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="p-2.5 rounded-full bg-white/20 flex-shrink-0">
                {pendingBalance.ordersCount > 0 ? (
                  <Wallet className="w-6 h-6 text-white" />
                ) : (
                  <CreditCard className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                {pendingBalance.ordersCount > 0 ? (
                  <>
                    <p className="font-bold text-white text-base md:text-lg">
                      Hai {pendingBalance.ordersCount} ordin{pendingBalance.ordersCount === 1 ? 'e' : 'i'} da incassare (€{pendingBalance.amount.toFixed(2)})
                    </p>
                    <p className="text-white/90 text-sm">
                      Attiva Stripe per ricevere i pagamenti sul tuo conto
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-bold text-white text-base md:text-lg">
                      Un ultimo passo per iniziare a vendere
                    </p>
                    <p className="text-white/90 text-sm">
                      Attiva Stripe in pochi minuti e i tuoi prodotti saranno subito acquistabili.
                      I pagamenti arrivano direttamente sul tuo conto, senza passare da Rarifex.
                    </p>
                  </>
                )}
              </div>
            </div>
            <Button
              className={`flex-shrink-0 ${
                pendingBalance.ordersCount > 0
                  ? 'bg-white text-amber-600 hover:bg-white/90'
                  : 'bg-white text-red-600 hover:bg-white/90 font-semibold'
              }`}
              onClick={() => navigate('/dashboard?tab=artigiano&section=stripe-setup')}
              data-testid="crafter-home-stripe-cta"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Attiva ora
            </Button>
          </div>
        </motion.div>
      )}

      {/* Da Gestire Oggi */}
      {urgentItems.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Flame className="w-5 h-5 text-red-500" />
            Da Gestire Oggi
          </h2>
          <div className="space-y-2">
            {urgentItems.map((item, index) => (
              <button
                key={index}
                onClick={() => onNavigate(item.section)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${
                  item.color === 'red' ? 'bg-red-50 border border-red-200 hover:bg-red-100' :
                  item.color === 'yellow' ? 'bg-yellow-50 border border-yellow-200 hover:bg-yellow-100' :
                  'bg-green-50 border border-green-200 hover:bg-green-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full animate-pulse ${
                    item.color === 'red' ? 'bg-red-500' :
                    item.color === 'yellow' ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`} />
                  <span className={`font-medium ${
                    item.color === 'red' ? 'text-red-800' :
                    item.color === 'yellow' ? 'text-yellow-800' :
                    'text-green-800'
                  }`}>
                    {item.count} {item.label}
                  </span>
                </div>
                <span className={`text-sm font-medium ${
                  item.color === 'red' ? 'text-red-600' :
                  item.color === 'yellow' ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  Vai <ChevronRight className="w-4 h-4 inline" />
                </span>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Panoramica */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#008080]" />
          Panoramica
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button 
            onClick={() => onNavigate('orders')}
            className="bg-white p-4 rounded-xl border border-slate-200 hover:border-[#008080] hover:shadow-md transition-all text-center"
            data-testid="panoramica-ordini-attivi"
          >
            <p className="text-2xl font-bold text-[#008080]">{activeOrders.length}</p>
            <p className="text-xs text-slate-500 mt-1">Ordini da gestire</p>
          </button>
          <button 
            onClick={() => onNavigate('client-requests')}
            className="bg-white p-4 rounded-xl border border-slate-200 hover:border-[#DAA520] hover:shadow-md transition-all text-center"
            data-testid="panoramica-richieste-aperte"
          >
            <p className="text-2xl font-bold text-[#DAA520]">{unansweredRequests.length}</p>
            <p className="text-xs text-slate-500 mt-1">Richieste aperte</p>
          </button>
          <button 
            onClick={() => onNavigate('orders', 'completed')}
            className="bg-white p-4 rounded-xl border border-slate-200 hover:border-green-500 hover:shadow-md transition-all text-center"
            data-testid="panoramica-completati"
          >
            <p className="text-2xl font-bold text-green-600">{completedOrders.length}</p>
            <p className="text-xs text-slate-500 mt-1">Completati</p>
          </button>
          <button 
            onClick={() => onNavigate('orders', 'in_progress')}
            className="bg-white p-4 rounded-xl border border-slate-200 hover:border-purple-500 hover:shadow-md transition-all text-center"
            data-testid="panoramica-in-lavorazione"
          >
            <p className="text-2xl font-bold text-purple-600">{inProgressOrders.length}</p>
            <p className="text-xs text-slate-500 mt-1">In lavorazione</p>
          </button>
        </div>
      </motion.div>

      {/* Azioni Veloci */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#DAA520]" />
          Azioni Veloci
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button 
            variant="outline" 
            className="h-auto py-4 justify-start border-2 border-dashed border-[#008080] hover:bg-[#008080]/5"
            onClick={() => onNavigate('client-requests')}
          >
            <Send className="w-5 h-5 mr-3 text-[#008080]" />
            <span className="text-left">
              <span className="block font-semibold text-slate-800">Rispondi richieste</span>
              <span className="block text-xs text-slate-500">Invia una proposta</span>
            </span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 justify-start border-2 border-dashed border-[#DAA520] hover:bg-[#DAA520]/5"
            onClick={() => onNavigate('products')}
          >
            <Plus className="w-5 h-5 mr-3 text-[#DAA520]" />
            <span className="text-left">
              <span className="block font-semibold text-slate-800">Aggiungi prodotto</span>
              <span className="block text-xs text-slate-500">Espandi il catalogo</span>
            </span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 justify-start border-2 border-slate-200 hover:border-purple-500 hover:bg-purple-50"
            onClick={() => onNavigate('storefront')}
          >
            <Package className="w-5 h-5 mr-3 text-purple-600" />
            <span className="text-left">
              <span className="block font-semibold text-slate-800">Gestisci vetrina</span>
              <span className="block text-xs text-slate-500">Personalizza il profilo</span>
            </span>
          </Button>
        </div>
      </motion.div>

      {/* Analytics Section */}
      <CrafterAnalytics />

      {/* Attività Recente */}
      {recentActivity.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-400" />
              Attività Recente
            </h2>
            <div className="space-y-3">
              {recentActivity.slice(0, 5).map((activity, index) => (
                <div key={index} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                  <div className={`w-2 h-2 rounded-full ${
                    activity.type === 'order' ? 'bg-green-500' :
                    activity.type === 'request' ? 'bg-blue-500' :
                    activity.type === 'message' ? 'bg-purple-500' :
                    'bg-slate-400'
                  }`} />
                  <p className="text-sm text-slate-600 flex-1">{activity.message}</p>
                  <span className="text-xs text-slate-400">{activity.time}</span>
                </div>
              ))}
            </div>
          </Card>
        </motion.div>
      )}

      {/* Empty State */}
      {urgentItems.length === 0 && orders.length === 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="p-8 text-center bg-gradient-to-br from-[#008080]/5 to-[#DAA520]/5">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-800 mb-2">Tutto in ordine!</h3>
            <p className="text-slate-600 mb-6">Non ci sono attività urgenti. Puoi concentrarti su:</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button className="bg-[#008080]" onClick={() => onNavigate('products')}>
                <Plus className="w-4 h-4 mr-2" />Aggiungi prodotti
              </Button>
              <Button variant="outline" onClick={() => onNavigate('storefront')}>
                Migliora la vetrina
              </Button>
            </div>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
